import { round } from 'lodash';
import { filterCommissions, getAmount as getCommissionAmount } from './commissions';
import { getGrossCommissionValues } from './vouchers';
import { UNIT_DOLLARS } from './transaction_side_details';

export function getDeductionTierBasis(voucher, tier, transactionSide = null) {
  const outsideCommissionsGrossAmount = filterCommissions(voucher.commissions, transactionSide, true)
    .reduce((sum, commission) => sum + getCommissionAmount(commission, transactionSide), 0);

  const priorTiersAmount = filterDeductions(voucher.deductions, false, transactionSide)
    .filter(deduction => deduction.tier < tier)
    .reduce((sum, deduction) => sum + getAmount(deduction, transactionSide), 0);

  const grossCommissionAmount = getGrossCommissionValues(voucher, transactionSide).grossAmount;

  return grossCommissionAmount - outsideCommissionsGrossAmount - priorTiersAmount;
}

export function updateDeductions(voucher) {
  const deductions = voucher.deductions.map((deduction, index) => ({ ...deduction, index }));
  const { preSplitDeductions, houseDeductions } = partitionDeductions(deductions);

  const internalGrossAmount = getInternalGrossAmount(voucher);
  updateHouseDeductions(houseDeductions, internalGrossAmount);
  updatePreSplitDeductions(preSplitDeductions, internalGrossAmount);

  voucher.deductions = deductions;
}

function partitionDeductions(deductions) {
  const preSplitDeductions = [];
  const houseDeductions = [];

  for (const deduction of deductions) {
    const collection = deduction.deductFromHouse ? houseDeductions : preSplitDeductions;
    collection.push(deduction);
  }

  return { preSplitDeductions, houseDeductions };
}

function updateHouseDeductions(houseDeductions, internalGrossAmount) {
  for (const deduction of houseDeductions) {
    updateDeduction(deduction, internalGrossAmount);
  }

  fixRoundingErrors(houseDeductions);
}

function updatePreSplitDeductions(preSplitDeductions, internalGrossAmount) {
  const presplitDeductionsByTier = groupByTier(preSplitDeductions);

  let tierBasis = internalGrossAmount;
  for (const tier in presplitDeductionsByTier) {
    let tierAmount = 0;

    for (const deduction of presplitDeductionsByTier[tier]) {
      if (!deduction._destroy) {
        updateDeduction(deduction, tierBasis);
        tierAmount += deduction.amount;
      }
    }

    tierBasis -= tierAmount;
  }

  fixRoundingErrors(preSplitDeductions);
}

function groupByTier(deductions) {
  const deductionsByTier = {};

  for (const deduction of deductions) {
    deductionsByTier[deduction.tier] = deductionsByTier[deduction.tier] || [];
    deductionsByTier[deduction.tier].push(deduction);
  }

  return deductionsByTier;
}

function updateDeduction(deduction, internalGrossAmount) {
  if (deduction.unit === 'unit_dollars') {
    if (internalGrossAmount) {
      deduction.percent = deduction.amount / internalGrossAmount * 100;
    } else {
      deduction.percent = 0;
    }
  } else {
    deduction.percent = Math.max(0.0, Math.min(100.0, deduction.percent));
    deduction.amount = deduction.percent * internalGrossAmount / 100;
  }

  return deduction;
}

function getInternalGrossAmount(voucher, transactionSide = null) {
  const outsideCommissionAmount = filterCommissions(voucher.commissions, transactionSide, true).reduce(
    (sum, commission) => {
      sum += getCommissionAmount(commission, transactionSide);
      return sum;
    }, 0
  );

  return getGrossCommissionValues(voucher, transactionSide).grossAmount - outsideCommissionAmount;
}

function fixRoundingErrors(deductions) {
  const percentDeductions = deductions.filter(deduction => deduction.unit === 'unit_percent' && !deduction._destroy);

  if (percentDeductions.length > 0) {
    // Only modify amounts calculated from percents, never those amounts provided directly by the user
    let roundedTotal = 0;
    let unroundedTotal = 0;
    for (const deduction of percentDeductions) {
      unroundedTotal += deduction.amount;
      deduction.amount = round(deduction.amount, 2);
      roundedTotal += deduction.amount;
    }

    percentDeductions[0].amount += round(unroundedTotal - roundedTotal, 2);
  }
}

export function filterDeductions(deductions, deductFromHouse, transactionSide = null) {
  return deductions.filter(deduction => (
    (!transactionSide || deduction.transactionSideDetail?.transactionSide === transactionSide) &&
    !deduction._destroy && !!deduction.deductFromHouse === deductFromHouse
  ));
}

export function getAmount(deduction, transactionSide = null) {
  if (transactionSide) {
    const transactionSideDetail = (
      deduction.transactionSideDetail.transactionSide === transactionSide ? deduction.transactionSideDetail : null
    );
    return transactionSideDetail?.grossAmount || 0;
  }

  return deduction.amount;
}

export function getPercent(deduction, transactionSide = null) {
  if (transactionSide) {
    const transactionSideDetail = (
      deduction.transactionSideDetail.transactionSide === transactionSide ? deduction.transactionSideDetail : null
    );
    return transactionSideDetail?.grossPercent || 0;
  }

  return deduction.percent;
}

export function updateDeductionTransactionSideDetail(voucher, deduction, transactionSideDetail) {
  const internalGrossAmount = getInternalGrossAmount(voucher, transactionSideDetail.transactionSide);

  if (transactionSideDetail.grossUnit === UNIT_DOLLARS) {
    if (internalGrossAmount) {
      transactionSideDetail.grossPercent = transactionSideDetail.grossAmount / internalGrossAmount * 100;
    } else {
      transactionSideDetail.grossPercent = 0;
    }
  } else {
    transactionSideDetail.grossPercent = Math.max(0.0, Math.min(100.0, transactionSideDetail.grossPercent));
    transactionSideDetail.grossAmount = transactionSideDetail.grossPercent * internalGrossAmount / 100;
  }

  return {
    ...deduction,
    transactionSideDetail,
    amount: transactionSideDetail.grossAmount,
    unit: 'unit_dollars'
  };
}

export function getPreSplitDeductionsTotal(voucher, transactionSide = null) {
  if (!transactionSide) {
    return voucher.totalDeduction;
  }

  return voucher.deductions.reduce((sum, deduction) => {
    if (!deduction._destroy && !deduction.deductFromHouse) {
      sum += getAmount(deduction, transactionSide);
    }

    return sum;
  }, 0);
}
